import { Link } from "@inertiajs/react"
import { ArrowLeft, Volume2, VolumeX } from "lucide-react"

import { CoachAvatar } from "@/components/member-story/CoachAvatar"
import { cn } from "@/lib/utils"

/**
 * The interview's own chrome. This page is deliberately outside the member
 * portal shell (no sidebar, no tab bar) so nothing competes with the question
 * being asked — which means the way back has to live here.
 */
export function InterviewHeader({
  position,
  questionCount,
  readAloud,
  coach,
  showProgress = true,
}) {
  const progress = showProgress
    ? Math.round(((position - 1) / questionCount) * 100)
    : 0

  return (
    <header className="shrink-0 border-b border-zinc-200 bg-white">
      <div className="mx-auto flex w-full max-w-2xl items-center gap-3 px-4 py-3">
        <CoachAvatar
          src={coach?.avatarUrl}
          initials={coach?.initials}
          name={coach?.name}
          size="sm"
        />
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-semibold uppercase tracking-wider text-zinc-900">
            Member Stories
          </p>
          <p className="truncate text-xs text-zinc-500">
            {coach?.name ? `with ${coach.name}` : "Kingdom Factor"}
          </p>
        </div>

        {readAloud.supported && (
          <button
            type="button"
            onClick={readAloud.toggle}
            aria-pressed={readAloud.enabled}
            aria-label={
              readAloud.enabled ? "Stop reading questions aloud" : "Read questions aloud"
            }
            title={readAloud.enabled ? "Reading aloud is on" : "Read questions aloud"}
            className={cn(
              "inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kf-red/50",
              readAloud.enabled
                ? "bg-kf-red/10 text-kf-red"
                : "text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            )}
          >
            {readAloud.enabled ? (
              <Volume2 className="h-4 w-4" />
            ) : (
              <VolumeX className="h-4 w-4" />
            )}
          </button>
        )}

        <Link
          href="/app/member"
          className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Back to portal</span>
        </Link>
      </div>

      {showProgress && (
        <div className="mx-auto w-full max-w-2xl px-4 pb-3">
          <div className="mb-1.5 flex items-baseline justify-between">
            <p className="font-mono text-xs uppercase tracking-wider text-zinc-500">
              Question {position} of {questionCount}
            </p>
          </div>
          <div
            className="h-1 w-full overflow-hidden rounded-full bg-zinc-200"
            role="progressbar"
            aria-valuenow={position}
            aria-valuemin={1}
            aria-valuemax={questionCount}
            aria-label={`Question ${position} of ${questionCount}`}
          >
            <div
              className="h-full rounded-full bg-kf-red transition-[width] duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </header>
  )
}
